import { useRef, useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Vote, Heart, Shirt, MessageCircle, Palette } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Capacitor } from '@capacitor/core';
import { cn } from '@/lib/utils';
import { useScrollHide } from '@/hooks/useScrollHide';
import { triggerHaptic } from '@/hooks/useHaptics';
import { useFeatureFlags, type FeatureFlagId } from '@/hooks/useFeatureFlags';
import { useNavLuminance } from '@/hooks/useNavLuminance';
import { useNavExpansionSlot } from '@/contexts/NavExpansionContext';
import { useMultiSelectActive } from '@/contexts/MultiSelectContext';

const navItems = [
  { to: '/app', icon: Heart, label: 'Saves' },
  { to: '/app/try-on', icon: Shirt, label: 'Try On', flagId: 'try_on' as FeatureFlagId },
  { to: '/app/vote', icon: Vote, label: 'Polls', flagId: 'polls' as FeatureFlagId },
  { to: '/app/chat', icon: MessageCircle, label: 'Chat', flagId: 'chat' as FeatureFlagId },
  { to: '/app/studio', icon: Palette, label: 'Studio', matchPaths: ['/app/studio', '/app/studio/hairdresser', '/app/studio/profile', '/app/studio/celebrity'], flagId: 'studio' as FeatureFlagId },
];

export function BottomNav() {
  const navWrapperRef = useRef<HTMLDivElement>(null);
  const isVisible = useScrollHide(10, 500, ['/app/chat']);
  const location = useLocation();
  const { isEnabled } = useFeatureFlags();
  const navMode = useNavLuminance(navWrapperRef);
  const expansionSlot = useNavExpansionSlot();
  const isMultiSelectActive = useMultiSelectActive();
  const filteredNavItems = navItems.filter(item => 
    !item.flagId || isEnabled(item.flagId)
  );
  
  // Detect try-on generation for background tooltip
  const searchParams = new URLSearchParams(location.search);
  const isGenerating = location.pathname === '/app/try-on' && searchParams.get('generating') === 'true';
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (isGenerating) {
      setShowTooltip(true);
      const timer = setTimeout(() => setShowTooltip(false), 5000);
      return () => clearTimeout(timer);
    } else {
      setShowTooltip(false);
    }
  }, [isGenerating]);

  return (
    <div
      ref={navWrapperRef}
      data-nav-mode={navMode}
      className={cn(
        "fixed left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] z-50 transition-all duration-300 ease-out",
        Capacitor.isNativePlatform()
          ? "bottom-[env(safe-area-inset-bottom,1rem)]"
          : "bottom-4",
        "hide-on-keyboard",
        !isVisible && "translate-y-[calc(100%+2rem+env(safe-area-inset-bottom))]",
        isMultiSelectActive && "translate-y-[calc(100%+2rem+env(safe-area-inset-bottom))]"
      )}
    >
      <AnimatePresence>
        {showTooltip && (
          <motion.button
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.95 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            onClick={() => setShowTooltip(false)}
            className="absolute -top-12 left-4 right-4 z-10 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-primary text-primary-foreground text-[11px] font-semibold shadow-lg text-center"
          >
            Keep exploring – we'll let you know when it's ready!
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-primary rotate-45" />
          </motion.button>
        )}
      </AnimatePresence>
      <motion.nav className="nav-glass flex flex-col px-2 pb-2 pt-0 rounded-[28px] w-full">
        <AnimatePresence mode="wait">
          {expansionSlot && (
            <motion.div
              key="expansion-slot"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
              className="overflow-hidden"
            >
              <div>
                {expansionSlot}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div className="flex items-center w-full pt-2">
        {filteredNavItems.map((item) => {
          const matchPaths = 'matchPaths' in item ? item.matchPaths : [item.to];
          const isItemActive = matchPaths?.some(path => 
            path === location.pathname || location.pathname.startsWith(path + '/')
          ) || location.pathname === item.to;
          
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => triggerHaptic('light')}
              aria-current={isItemActive ? 'page' : undefined}
              className="nav-glass-link relative flex flex-col items-center gap-0.5 py-2 px-4 flex-1"
            >
              <item.icon
                className="w-5 h-5"
                strokeWidth={isItemActive ? 2.5 : 2}
              />
              <span className={cn(
                "text-[10px] whitespace-nowrap",
                isItemActive ? "font-semibold" : "font-medium"
              )}>
                {item.label}
              </span>
              {isItemActive && (
                <div className="nav-glass-dot absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full" />
              )}
            </NavLink>
          );
        })}
        </div>
      </motion.nav>
    </div>
  );
}
